import { useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { ContainerStatusBadge } from '@/components/StatusBadge';
import { api, ApiError } from '@/services/api';
import type { Site, Tenant } from '@/types';

export function OverviewTab({ site, isAdmin }: { site: Site; isAdmin: boolean }) {
  const fields: { label: string; value: React.ReactNode }[] = [
    { label: 'Identifier', value: <span className="font-mono">{site.identifier}</span> },
    { label: 'Status', value: <ContainerStatusBadge status={site.containerStatus} /> },
    { label: 'Template', value: site.template?.name ?? '—' },
    { label: 'Repository', value: site.repoUrl ? (
      <a href={site.repoUrl} target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:underline">
        {site.repoUrl}
      </a>
    ) : '—' },
    {
      label: 'Letztes Deployment',
      value: site.lastDeployedAt
        ? new Date(site.lastDeployedAt).toLocaleString('de-DE')
        : 'Nie',
    },
    { label: 'Domains', value: String(site.domains?.length ?? 0) },
    { label: 'Widgets', value: String(site.siteWidgets?.length ?? 0) },
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-surface-300 bg-surface-100">
        <dl className="divide-y divide-surface-300">
          {fields.map(({ label, value }) => (
            <div key={label} className="flex flex-col px-5 py-4 sm:flex-row sm:items-center">
              <dt className="w-40 shrink-0 text-sm text-neutral-500">{label}</dt>
              <dd className="text-sm text-neutral-200">{value}</dd>
            </div>
          ))}
        </dl>
      </div>

      {isAdmin && <WawiIntegrationCard tenantId={site.tenantId} />}
    </div>
  );
}

function WawiIntegrationCard({ tenantId }: { tenantId: string }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [token, setToken] = useState('');
  const [tokenSet, setTokenSet] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    api
      .get<Tenant>(`/tenants/${tenantId}`)
      .then((tenant) => {
        if (cancelled) return;
        setEnabled(tenant.wawiEnabled ?? false);
        setTokenSet(tenant.wawiTokenSet ?? false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof ApiError ? err.message : 'Tenant konnte nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSuccess(false);
    try {
      const trimmed = token.trim();
      // Empty token field means "leave the stored token unchanged".
      await api.patch<Tenant>(`/tenants/${tenantId}`, {
        wawiEnabled: enabled,
        ...(trimmed ? { wawiWidgetToken: trimmed } : {}),
      });
      if (trimmed) {
        setTokenSet(true);
        setToken('');
      }
      setSuccess(true);
    } catch (err: unknown) {
      setSaveError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const handleClearToken = async () => {
    setSaving(true);
    setSaveError(null);
    setSuccess(false);
    try {
      await api.patch<Tenant>(`/tenants/${tenantId}`, { wawiWidgetToken: null });
      setTokenSet(false);
      setToken('');
      setSuccess(true);
    } catch (err: unknown) {
      setSaveError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-surface-300 bg-surface-100">
      <div className="border-b border-surface-300 px-5 py-4">
        <h2 className="text-sm font-semibold text-neutral-100">WaWi-Integration</h2>
        <p className="mt-1 text-xs text-neutral-500">
          Der Widget-Token wird in der WaWi-Verwaltung erzeugt (Widget → Token). Er bleibt
          serverseitig und wird nie an Besucher ausgeliefert.
        </p>
      </div>

      <div className="px-5 py-4">
        {loading ? (
          <div className="flex h-20 items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          </div>
        ) : loadError ? (
          <p className="text-sm text-red-400">{loadError}</p>
        ) : (
          <div className="space-y-4">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => {
                  setEnabled(e.target.checked);
                  setSuccess(false);
                }}
                className="h-4 w-4 rounded border-surface-400 bg-surface-200 text-brand-500 focus:ring-1 focus:ring-brand-500"
              />
              <span className="text-sm text-neutral-200">WaWi-Anbindung aktivieren</span>
            </label>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-neutral-300">
                Widget-Token{' '}
                <span className={tokenSet ? 'text-green-400' : 'text-neutral-500'}>
                  ({tokenSet ? 'hinterlegt' : 'nicht hinterlegt'})
                </span>
              </label>
              <div className="relative">
                <input
                  type={showToken ? 'text' : 'password'}
                  value={token}
                  onChange={(e) => {
                    setToken(e.target.value);
                    setSuccess(false);
                  }}
                  placeholder={tokenSet ? 'Leer lassen = unverändert' : 'WaWi-Widget-Token'}
                  autoComplete="off"
                  className="w-full rounded-lg border border-surface-400 bg-surface-200 px-3 py-2 pr-10 text-sm text-neutral-200 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
                <button
                  type="button"
                  onClick={() => setShowToken((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-neutral-500 hover:text-neutral-300"
                  aria-label={showToken ? 'Token verbergen' : 'Token anzeigen'}
                >
                  {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {tokenSet && (
                <button
                  type="button"
                  onClick={handleClearToken}
                  disabled={saving}
                  className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50"
                >
                  Token entfernen
                </button>
              )}
            </div>

            {saveError && <p className="text-sm text-red-400">{saveError}</p>}
            {success && <p className="text-sm text-green-400">Gespeichert.</p>}

            <div>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Speichern…' : 'Speichern'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
